"use client"

import { useState } from "react"
import Nav from "@/components/nav"
import Footer from "@/components/footer"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ShoppingBag, Star, Leaf, Flame, Filter } from "lucide-react"
import Link from "next/link"

/* ─── DATA ─────────────────────────────────── */

const products = [
  {
    id: 1,
    name: "Ethiopian Yirgacheffe",
    origin: "Yirgacheffe, Ethiopia",
    roast: "Light",
    process: "Washed",
    notes: ["Blueberry", "Jasmine", "Lemon Zest"],
    altitude: "1,800–2,200m",
    price: 19,
    per: "12 oz",
    badge: "Staff Pick",
    badgeColor: "bg-primary/15 text-primary border-primary/20",
    image: "/images/coffee-ethiopia-579754.jpg",
    story: "Grown at altitude in the birthplace of coffee. Bright, floral, and unforgettable.",
    roastColor: "text-amber-500",
    slug: "ethiopian-yirgacheffe",
  },
  {
    id: 2,
    name: "Guatemala Antigua",
    origin: "Antigua, Guatemala",
    roast: "Medium",
    process: "Washed",
    notes: ["Brown Sugar", "Dark Cherry", "Hazelnut"],
    altitude: "1,500–1,700m",
    price: 17,
    per: "12 oz",
    badge: "Seasonal",
    badgeColor: "bg-secondary/15 text-secondary border-secondary/20",
    image: "/images/coffee-guatemala-579766.jpg",
    story: "From volcanic valleys at 5,000 feet. Consistent, balanced, and endlessly drinkable.",
    roastColor: "text-orange-500",
    slug: "guatemala-antigua",
  },
  {
    id: 3,
    name: "Sumatra Mandheling",
    origin: "Mandheling, North Sumatra",
    roast: "Dark",
    process: "Wet-Hulled",
    notes: ["Cedar", "Dark Chocolate", "Earthy"],
    altitude: "1,000–1,500m",
    price: 17,
    per: "12 oz",
    badge: "Bold & Rich",
    badgeColor: "bg-foreground/10 text-foreground border-foreground/15",
    image: "/images/coffee-sumatra-579781.jpg",
    story: "Wet-hulled in the Mandheling highlands. Ancient, earthy, and unlike anything else.",
    roastColor: "text-stone-700",
    slug: "sumatra-mandheling",
  },
  {
    id: 4,
    name: "Yirgacheffe Natural",
    origin: "Guji Zone, Ethiopia",
    roast: "Light",
    process: "Natural",
    notes: ["Strawberry Jam", "Rose", "Cardamom"],
    altitude: "1,900–2,300m",
    price: 22,
    per: "12 oz",
    badge: "Limited",
    badgeColor: "bg-rose-50 text-rose-700 border-rose-200",
    image: "/images/coffee-ethiopia-579754.jpg",
    story: "A naturally processed lot from the Guji highlands. Fruit-forward and gloriously complex.",
    roastColor: "text-amber-500",
    slug: "yirgacheffe-natural",
  },
  {
    id: 5,
    name: "Origin Sampler Box",
    origin: "Ethiopia · Guatemala · Sumatra",
    roast: "Mixed",
    process: "Various",
    notes: ["Three Origins", "Full Spectrum", "Perfect Gift"],
    altitude: "Various",
    price: 48,
    per: "3 × 6 oz",
    badge: "Best Gift",
    badgeColor: "bg-primary/15 text-primary border-primary/20",
    image: "/images/coffee-guatemala-579766.jpg",
    story: "One box, three continents. The definitive Northwind tasting experience.",
    roastColor: "text-primary",
    slug: "origin-sampler",
  },
  {
    id: 6,
    name: "House Blend",
    origin: "Ethiopia & Guatemala",
    roast: "Medium",
    process: "Washed Blend",
    notes: ["Caramel", "Milk Chocolate", "Dried Fig"],
    altitude: "1,500–2,000m",
    price: 16,
    per: "12 oz",
    badge: "Everyday",
    badgeColor: "bg-secondary/15 text-secondary border-secondary/20",
    image: "/images/coffee-sumatra-579781.jpg",
    story: "Our most approachable roast. Works at 6am, at the office, and after dinner.",
    roastColor: "text-orange-500",
    slug: "house-blend",
  },
]

const roastFilters = ["All", "Light", "Medium", "Dark", "Mixed"]

const roastIcon = (roast: string) => {
  if (roast === "Light") return <Leaf className="w-3.5 h-3.5" />
  if (roast === "Dark") return <Flame className="w-3.5 h-3.5" />
  return <Star className="w-3.5 h-3.5" />
}

export default function ShopPage() {
  const [activeRoast, setActiveRoast] = useState("All")
  const [added, setAdded] = useState<number | null>(null)

  const filtered = activeRoast === "All" ? products : products.filter((p) => p.roast === activeRoast)

  const handleAdd = (id: number) => {
    setAdded(id)
    setTimeout(() => setAdded(null), 1800)
  }

  return (
    <>
      <Nav />

      {/* ── Hero ─────────────────────────────────── */}
      <section className="relative pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/hero-roaster-579737.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#1A0D08]/80" />
        </div>
        <div className="absolute inset-0 dot-grid opacity-15" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <Badge className="mb-5 bg-primary/20 text-primary border-primary/30 rounded-full px-4 py-1.5 text-xs tracking-widest uppercase">
            Roasted to Order
          </Badge>
          <h1 className="font-heading text-5xl md:text-7xl font-black text-white leading-[1.05] mb-5">
            The <span className="gradient-text">Roastery</span> Shop
          </h1>
          <p className="text-white/65 text-lg max-w-lg mx-auto leading-relaxed">
            Every bag leaves Portland within 48 hours of roasting. No warehouses. No mystery dates.
            Just coffee at peak flavour.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
            <path d="M0 60L1440 60L1440 0C1200 50 840 70 720 40C600 10 240 55 0 0L0 60Z" fill="hsl(43 67% 97%)" />
          </svg>
        </div>
      </section>

      {/* ── Shop ─────────────────────────────────── */}
      <main className="bg-background py-16">
        <div className="max-w-7xl mx-auto px-6">

          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-3 mb-12">
            <span className="flex items-center gap-1.5 text-sm text-muted-foreground font-medium">
              <Filter className="w-4 h-4" /> Roast
            </span>
            {roastFilters.map((r) => (
              <button
                key={r}
                onClick={() => setActiveRoast(r)}
                className={[
                  "px-4 py-1.5 rounded-full text-sm font-medium border transition-all",
                  activeRoast === r
                    ? "bg-primary text-primary-foreground border-primary shadow-sm"
                    : "bg-background text-muted-foreground border-border hover:border-primary/40 hover:text-foreground",
                ].join(" ")}
              >
                {r}
              </button>
            ))}
            <span className="ml-auto text-sm text-muted-foreground">{filtered.length} coffees</span>
          </div>

          {/* Product grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((product) => (
              <div
                key={product.id}
                className="group rounded-2xl border border-border/50 bg-background overflow-hidden hover:border-primary/30 hover:-translate-y-1 transition-all duration-300"
                style={{ boxShadow: "0 2px 12px hsl(15 48% 12% / 0.07)" }}
              >
                {/* Image */}
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A0D08]/60 to-transparent" />
                  <Badge className={`absolute top-4 left-4 text-xs border ${product.badgeColor}`}>
                    {product.badge}
                  </Badge>
                  <div className="absolute bottom-4 left-4 right-4 flex gap-1.5 flex-wrap">
                    {product.notes.map((n) => (
                      <span key={n} className="text-xs text-white/80 bg-black/30 backdrop-blur-sm rounded-full px-2.5 py-0.5">
                        {n}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="font-heading text-xl font-bold text-foreground leading-snug group-hover:text-primary transition-colors">
                      {product.name}
                    </h3>
                    <span className="text-xl font-bold text-foreground shrink-0">${product.price}</span>
                  </div>

                  <p className="text-xs text-muted-foreground mb-3">{product.origin} · {product.per}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-5">{product.story}</p>

                  <div className="flex items-center gap-4 text-xs text-muted-foreground mb-5">
                    <span className={`flex items-center gap-1 font-medium ${product.roastColor}`}>
                      {roastIcon(product.roast)} {product.roast}
                    </span>
                    <span>{product.process}</span>
                    <span>{product.altitude}</span>
                  </div>

                  <Button
                    className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm"
                    onClick={() => handleAdd(product.id)}
                  >
                    {added === product.id ? (
                      "Added ✓"
                    ) : (
                      <>
                        <ShoppingBag className="w-4 h-4 mr-2" /> Add to Cart
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Freshness guarantee */}
          <div className="mt-20 rounded-2xl bg-accent border border-border/50 p-10 md:p-14 text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              The Northwind Freshness Guarantee
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto leading-relaxed mb-8">
              Every order is roasted within 24 hours of payment and shipped the next business day. If your
              coffee arrives more than 5 days post-roast, we'll re-roast and re-ship for free.
            </p>
            <div className="flex flex-wrap justify-center gap-8 mb-8">
              {[
                { label: "Roasted", value: "Day 0" },
                { label: "Shipped", value: "Day 1" },
                { label: "At your door", value: "Day 2–4" },
                { label: "Peak flavour window", value: "Day 7–28" },
              ].map((step) => (
                <div key={step.label} className="text-center">
                  <div className="text-2xl font-bold font-heading text-primary mb-1">{step.value}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">{step.label}</div>
                </div>
              ))}
            </div>
            <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-8 shadow-md">
              <Link href="/subscribe">Save with a Subscription →</Link>
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </>
  )
}
